import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { arEG, enUS } from '@mui/material/locale';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from '@mui/stylis-plugin-rtl';

// project imports
import useConfig from 'hooks/useConfig';
import { dir, isRtl } from 'i18n';
import CustomShadows from './custom-shadows';
import componentsOverride from './overrides';
import { buildPalette } from './palette';
import Typography from './typography';

// Emotion cache: in Arabic, the RTL plugin mirrors every MUI/sx style (margin-left -> margin-right,
// etc.). `prepend` keeps MUI styles first so our own CSS still wins, like StyledEngineProvider's
// injectFirst did before.
const emotionCache = createCache({
  key: isRtl ? 'muirtl' : 'mui',
  prepend: true,
  stylisPlugins: isRtl ? [prefixer, rtlPlugin] : [prefixer]
});

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
  const { state } = useConfig();

  // Public Sans has no Arabic glyphs — Cairo first in Arabic (it also covers Latin text).
  const fontFamily = isRtl ? `'Cairo', ${state.fontFamily}` : state.fontFamily;
  const themeTypography = useMemo(() => Typography(fontFamily), [fontFamily]);

  const palette = useMemo(() => buildPalette(state.presetColor), [state.presetColor]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: dir,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      typography: themeTypography,
      colorSchemes: {
        light: {
          palette: palette.light,
          customShadows: CustomShadows(palette.light, 'light')
        }
      },
      cssVariables: {
        cssVarPrefix: '',
        colorSchemeSelector: 'data-color-scheme'
      }
    }),
    [themeTypography, palette]
  );

  // Second argument: MUI's built-in component texts (pagination "Rows per page", etc.).
  const themes = createTheme(themeOptions, isRtl ? arEG : enUS);
  themes.components = { ...themes.components, ...componentsOverride(themes) };

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider disableTransitionOnChange theme={themes} modeStorageKey="theme-mode" defaultMode="light">
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
